import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { MaskInput } from 'components/MaskInput'
import { Radio } from 'components/v2/Radio'
import { paymentMethodMessages } from 'features/Bookings/components/PaymentMethod/messages'
import { safeFormatMessage } from 'utils/intl'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import {
  GTBookingV2,
  GTBookingV2PaymentMethod,
  GTBookingV2PaymentProcessor,
  GTBookingV2PaymentStatus
} from 'gql/types.generated'
import { ReactElement } from 'react'
import { FormikProps } from 'formik'

const messages = defineMessages({
  last4Placeholder: {
    id: 'bookings.edit.payments.last4.placeholder',
    defaultMessage: 'Last4'
  }
})

const RadiosWrapper = styled.div`
  white-space: nowrap;
`

const EditPaymentMethod = ({ payment, paymentIndex, ...props }: TSFixMe): TSFixMe => {
  const handleChange = ({ target: { id } }: TSFixMe): TSFixMe => {
    props.onChange(id)
  }

  return (
    <RadiosWrapper>
      <Radio
        id={GTBookingV2PaymentMethod.Cash}
        name={`payments[${paymentIndex}].method`}
        checked={payment.method === GTBookingV2PaymentMethod.Cash}
        onChange={handleChange}
      >
        <FormattedMessage id="dictionary.cash" defaultMessage="Cash" />
      </Radio>
      <Radio
        id={GTBookingV2PaymentMethod.Card}
        name={`payments[${paymentIndex}].method`}
        checked={payment.method === GTBookingV2PaymentMethod.Card}
        onChange={handleChange}
      >
        <FormattedMessage id="dictionary.card" defaultMessage="Card" />
      </Radio>
    </RadiosWrapper>
  )
}

const PaymentMethod = ({ payment = {} }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  if (payment.status === GTBookingV2PaymentStatus.Paid) {
    if (payment.processor === GTBookingV2PaymentProcessor.Conekta) {
      return (
        <CopyToClipboardWrapper>
          <div>{payment.chargeID.substring(0, 8)}</div>
          <CopyToClipboard str={payment.chargeID} />
        </CopyToClipboardWrapper>
      )
    } else {
      return <div>{safeFormatMessage({ intl, messages: paymentMethodMessages, key: payment.method })}</div>
    }
  }

  return null
}

const canEditLast4 = (payment: TSFixMe): TSFixMe => {
  return (
    payment &&
    payment.status === GTBookingV2PaymentStatus.Paid &&
    payment.method === GTBookingV2PaymentMethod.Card &&
    payment.processor === GTBookingV2PaymentProcessor.Clip
  )
}

const Label = styled.label<TSFixMe>`
  input {
    min-width: 33px;
  }
`

const EditLast4Wrapper = styled.div<TSFixMe>`
  margin-top: 5px;
`

const EditLast4 = ({ payment, paymentIndex, ...props }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  return (
    <Label>
      <MaskInput
        type="tel"
        lazy={isEmpty((payment as TSFixMe).last4)}
        id={`payments[${paymentIndex}].last4`}
        value={payment.last4}
        onAccept={(value: TSFixMe): TSFixMe => props.setFieldValue(`payments[${paymentIndex}].last4`, value)}
        onBlur={props.handleBlur}
        placeholder={intl.formatMessage(messages.last4Placeholder)}
        mask="0000"
        touched={
          props.touched.payments && props.touched.payments[paymentIndex] && props.touched.payments[paymentIndex].last4
        }
        errors={
          props.errors.payments && props.errors.payments[paymentIndex] && props.errors.payments[paymentIndex].last4
        }
        small
      />
    </Label>
  )
}

const PaymentMethodWrapper = styled.div<TSFixMe>`
  display: flex;
  margin: 0 -5px;

  > * {
    margin: 0 5px;
  }
`

const Table = styled.table<TSFixMe>`
  width: 100%;
  border-collapse: collapse;
`

const Tr = styled.tr<TSFixMe>`
  border-bottom: 1px solid #d8d8d8;

  &:last-child {
    border-bottom: none;
  }
`

const Th = styled.th<TSFixMe>`
  text-align: left;
  padding: ${(props): TSFixMe => (props.isFirst ? '10px 10px 10px 0' : '10px')};
  font-size: 16px;
  font-weight: 500;
  color: #1b1b3d;
  border-bottom: 1px solid #666666;
`

const Td = styled.td<TSFixMe>`
  font-size: 16px;
  color: #1b1b3d;
  padding: ${(props): TSFixMe => (props.isFirst ? '15px 10px 15px 0' : '15px 10px')};
  vertical-align: top;
`

interface IBookingPaymentsMethodProps extends FormikProps<TSFixMe> {
  editPriceDisabled?: boolean
  booking: GTBookingV2
}

export const BookingPaymentsMethod = ({
  editPriceDisabled,
  booking,
  ...props
}: IBookingPaymentsMethodProps): ReactElement | null => {
  if (isEmpty(props.values.payments)) return null

  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined

  return (
    <Table>
      <thead>
        <tr>
          <Th isFirst>
            <FormattedMessage id="bookings.edit.paymentsMethod.paymentId" defaultMessage="Payment ID" />
          </Th>
          <Th>
            <FormattedMessage id="bookings.edit.paymentsMethod.amount" defaultMessage="Amount" />
          </Th>
          <Th>
            <FormattedMessage id="bookings.edit.paymentsMethod.method" defaultMessage="Method" />
          </Th>
        </tr>
      </thead>
      <tbody>
        {props.values.payments.map((payment: TSFixMe, paymentIndex: TSFixMe): TSFixMe => {
          if (payment.status !== GTBookingV2PaymentStatus.Paid) return null

          const canEditPaymentMethod =
            payment &&
            payment.status === GTBookingV2PaymentStatus.Paid &&
            payment.processor !== GTBookingV2PaymentProcessor.Conekta &&
            !editPriceDisabled
          const handlePaymentMethodChange = (paymentMethod: TSFixMe): TSFixMe => {
            props.setFieldValue(`payments[${paymentIndex}].method`, paymentMethod)

            if (paymentMethod === GTBookingV2PaymentMethod.Card) {
              props.setFieldValue(`payments[${paymentIndex}].processor`, GTBookingV2PaymentProcessor.Clip)
              props.setFieldValue(`payments[${paymentIndex}].last4`, '')
            }

            if (paymentMethod === GTBookingV2PaymentMethod.Cash) {
              props.setFieldValue(`payments[${paymentIndex}].last4`, null)
              props.setFieldValue(`payments[${paymentIndex}].processor`, null)
            }
          }
          const handleLast4Change = ({ target: { value } }: TSFixMe): TSFixMe => {
            props.setFieldValue(`payments[${paymentIndex}].last4`, value)
          }

          return (
            <Tr key={payment.id}>
              <Td isFirst>
                <CopyToClipboardWrapper>
                  <div>{payment.id.substring(0, 8)}</div>
                  <CopyToClipboard str={payment.id} />
                </CopyToClipboardWrapper>
              </Td>
              <Td>
                <FormattedPrice value={payment.amount} currency={pricesCurrency} />
              </Td>
              <Td>
                {canEditPaymentMethod ? (
                  <PaymentMethodWrapper>
                    <EditPaymentMethod
                      payment={payment}
                      paymentIndex={paymentIndex}
                      onChange={handlePaymentMethodChange}
                      {...props}
                    />
                    {canEditLast4(payment) && (
                      <EditLast4Wrapper>
                        <EditLast4
                          payment={payment}
                          paymentIndex={paymentIndex}
                          onChange={handleLast4Change}
                          {...props}
                        />
                      </EditLast4Wrapper>
                    )}
                  </PaymentMethodWrapper>
                ) : (
                  <div>
                    <PaymentMethod payment={payment} />
                  </div>
                )}
              </Td>
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}
